import React, { useState } from 'react'
import { Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot field
  })

  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState('idle') // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('')

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Navn er påkrævet'
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer er påkrævet'
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Indtast venligst et gyldigt telefonnummer'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-mail er påkrævet'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Indtast venligst en gyldig e-mailadresse'
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Beskrivelse er påkrævet'
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Beskrivelsen skal være på mindst 10 tegn'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validate()) return

    setStatus('submitting')
    setErrorMessage('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        })
      } else {
        throw new Error('Der skete en fejl ved afsendelsen. Prøv venligst igen.')
      }
    } catch (err) {
      setStatus('error')
      setErrorMessage(err.message || 'Der kunne ikke oprettes forbindelse til serveren.')
    }
  }

  return (
    <div className="border border-stone-200 bg-[#f4f1ea] p-8 md:p-10 rounded-none shadow-none">
      {status === 'success' ? (
        <div className="flex flex-col items-center justify-center py-12 text-center animate-fade-in">
          <div className="mb-6 rounded-none border border-[#b89a6c]/30 p-4 text-[#b89a6c]">
            <CheckCircle2 size={40} className="stroke-[1.5]" />
          </div>
          <h3 className="font-serif text-2xl font-light text-[#1c2b21] tracking-wide">Tak for din henvendelse!</h3>
          <p className="mt-4 max-w-md text-stone-600 text-sm font-light leading-relaxed">
            Vi har modtaget din besked og vender tilbage til dig hurtigst muligt, typisk inden for 24 timer.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-8 bg-[#b89a6c] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#1c2b21] transition-colors cursor-pointer rounded-none"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <h3 className="font-serif text-2xl font-light text-[#1c2b21] tracking-wide">Bestil hurtigt</h3>
            <p className="text-xs text-stone-600 font-light leading-relaxed">
              Udfyld formularen nedenfor, så vender vi tilbage med et uforpligtende tilbud på din madbestilling.
            </p>
          </div>

          {/* Antispam Honeypot - Absolutely hidden from humans */}
          <div className="sr-only absolute -z-10 overflow-hidden h-0 w-0" aria-hidden="true">
            <label htmlFor="website_url">Efterlad dette felt tomt</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Name & Phone Grid */}
          <div className="grid gap-6 sm:grid-cols-2">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-[10px] uppercase tracking-widest text-stone-600 font-light mb-2">
                Navn <span className="text-[#b89a6c]">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className={`w-full bg-white text-neutral-900 placeholder-stone-400 px-4 py-3 text-sm outline-none transition-all border rounded-none ${
                  errors.name ? 'border-red-500 bg-red-50/30' : 'border-stone-200 focus:border-[#b89a6c]'
                }`}
              />
              {errors.name && (
                <p className="mt-1.5 flex items-center gap-1 text-[11px] font-light text-red-500">
                  <AlertCircle size={12} /> {errors.name}
                </p>
              )}
            </div>

            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block text-[10px] uppercase tracking-widest text-stone-600 font-light mb-2">
                Telefonnummer <span className="text-[#b89a6c]">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 45489981"
                className={`w-full bg-white text-neutral-900 placeholder-stone-400 px-4 py-3 text-sm outline-none transition-all border rounded-none ${
                  errors.phone ? 'border-red-500 bg-red-50/30' : 'border-stone-200 focus:border-[#b89a6c]'
                }`}
              />
              {errors.phone && (
                <p className="mt-1.5 flex items-center gap-1 text-[11px] font-light text-red-500">
                  <AlertCircle size={12} /> {errors.phone}
                </p>
              )}
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-[10px] uppercase tracking-widest text-stone-600 font-light mb-2">
              E-mailadresse <span className="text-[#b89a6c]">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="din@email.dk"
              className={`w-full bg-white text-neutral-900 placeholder-stone-400 px-4 py-3 text-sm outline-none transition-all border rounded-none ${
                errors.email ? 'border-red-500 bg-red-50/30' : 'border-stone-200 focus:border-[#b89a6c]'
              }`}
            />
            {errors.email && (
              <p className="mt-1.5 flex items-center gap-1 text-[11px] font-light text-red-500">
                <AlertCircle size={12} /> {errors.email}
              </p>
            )}
          </div>

          {/* Description Field */}
          <div>
            <label htmlFor="description" className="block text-[10px] uppercase tracking-widest text-stone-600 font-light mb-2">
              Besked / Beskrivelse af din forespørgsel <span className="text-[#b89a6c]">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={5}
              placeholder="Skriv lidt om dit arrangement, antal gæster, dato og hvilken menu du ønsker..."
              className={`w-full bg-white text-neutral-900 placeholder-stone-400 px-4 py-3 text-sm outline-none transition-all border rounded-none ${
                errors.description ? 'border-red-500 bg-red-50/30' : 'border-stone-200 focus:border-[#b89a6c]'
              }`}
            />
            {errors.description && (
              <p className="mt-1.5 flex items-center gap-1 text-[11px] font-light text-red-500">
                <AlertCircle size={12} /> {errors.description}
              </p>
            )}
          </div>

          {/* Error Message */}
          {status === 'error' && (
            <div className="flex items-start gap-2 border border-red-200 bg-red-50 p-4 text-sm text-red-600 rounded-none">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-xs uppercase tracking-wider">Der opstod en fejl</p>
                <p className="text-xs font-light mt-1">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="flex w-full items-center justify-center gap-2 bg-[#b89a6c] text-white py-3.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#1c2b21] transition-colors disabled:pointer-events-none disabled:opacity-50 cursor-pointer rounded-none shadow-none"
          >
            {status === 'submitting' ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Sender forespørgsel...
              </>
            ) : (
              <>
                <Send size={14} />
                Send besked
              </>
            )}
          </button>
        </form>
      )}
    </div>
  )
}
